/* GASSISA
=========================================================================================
* Descripción: Lector de códigos de barras para entradas/salidas con técnica del
*              lector de conteos: input controlado por ref, Enter dispara el escaneo
*              de inmediato y limpia, autofocus agresivo y bloqueo de teclas
*              inválidas para no perder lecturas en ráfaga. Sin cola en
*              localStorage: el respaldo es el buffer Redis del backend.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/09/2026 | LIC. José Luis Aragón C.                       | Creación (técnica Conteos)
*      2       | 23/09/2026 | LIC. José Luis Aragón C.                       | Mismo cascarón/estilo del colector
*              |            |                                  | anterior (clases entradas-salidas)
*      3       | 23/09/2026 | LIC. José Luis Aragón C.                       | Atajos con Alt/Ctrl/Meta burbujean
*              |            |                                  | al módulo (Alt+F1/A/I/F12 vivos)
 *      4       | 23/09/2026 | LIC. José Luis Aragón C.                       | Deshabilita autocomplete del lector
 *      5       | 25/09/2026 | LIC. José Luis Aragón C.                       | Retiene código + select para
 *              |            |                                  | repetir (Enter) / sobrescribir
 * ---------------------------------------------------------------------------------------
 */

import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentPaste from '@mui/icons-material/ContentPaste';
import { leerTextoPortapapeles } from '../../helpers/portapapeles';

const SizeSoftXLectorCodigoBarrasEntradas = forwardRef(function SizeSoftXLectorCodigoBarrasEntradas(props, ref) {
  const { disabled, onColectorScan, placeholder } = props;

  const inputRef = useRef(null);

  const [barcode, setBarcode] = useState("");
  const barcodeRef = useRef("");
  // Rev 5: retener el código tras el escaneo para repetirlo (Enter repetido
  // suma otra unidad). El valor queda SELECCIONADO para que la próxima
  // lectura/tec leo lo sobrescriba en vez de concatenarlo.
  const justScannedRef = useRef(false);

  const setBarcodeValue = (value) => {
    barcodeRef.current = value;
    setBarcode(value);
  };

  const seleccionarLector = () => {
    const el = inputRef.current;
    if (!el || el.disabled) return;
    requestAnimationFrame(() => {
      try {
        el.focus();
        el.click?.();
        el.select?.();
      } catch {}
    });
  };

  const tieneSeleccionTotal = () => {
    const el = inputRef.current;
    if (!el || typeof el.selectionStart !== "number") return false;
    const len = (barcodeRef.current || "").length;
    return len > 0 && el.selectionStart === 0 && el.selectionEnd === len;
  };

  const handleChangeBarcode = (event) => {
    justScannedRef.current = false;
    setBarcodeValue(event.target.value);
  };

  const handlePegarPortapapeles = async () => {
    if (disabled) return;
    try {
      const texto = await leerTextoPortapapeles();
      justScannedRef.current = false;
      if (texto) setBarcodeValue(texto);
    } catch {
      justScannedRef.current = false;
    }
    seleccionarLector();
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      inputRef.current?.click?.();
      inputRef.current?.select?.();
    },
    clear: () => {
      justScannedRef.current = false;
      setBarcodeValue('');
    },
    getValue: () => barcodeRef.current,
  }), []);

  useEffect(() => {
    const focusBarcode = () => {
      const el = inputRef.current;
      if (!el || el.disabled) return;

      if (document.activeElement === el) return;

      el.focus();
      el.click?.();
      el.select?.();
    };

    focusBarcode();

    const shouldIgnoreTarget = (target) => {
      if (!target) return false;
      const interactive = target.closest?.(
        'input, textarea, select, button, [contenteditable="true"], [role="option"], [role="listbox"], [role="dialog"], [role="button"], .MuiAutocomplete-popper'
      );
      return Boolean(interactive) && target.id !== "txtCodigoBarras";
    };

    const onAnyPointer = (e) => {
      if (disabled) return;
      if (shouldIgnoreTarget(e.target)) return;
      requestAnimationFrame(() => focusBarcode());
    };

    document.addEventListener("pointerdown", onAnyPointer, true);
    document.addEventListener("mousedown", onAnyPointer, true);
    document.addEventListener("touchstart", onAnyPointer, true);
    window.addEventListener("focus", focusBarcode);

    return () => {
      document.removeEventListener("pointerdown", onAnyPointer, true);
      document.removeEventListener("mousedown", onAnyPointer, true);
      document.removeEventListener("touchstart", onAnyPointer, true);
      window.removeEventListener("focus", focusBarcode);
    };
  }, [disabled]);

  const handleKeyDown = (event) => {
    // Rev 3 (23/09/2026): los atajos con modificadores (Alt+F1, Alt+A, Alt+I,
    // Alt+F12...) pertenecen al módulo: se dejan burbujear al handler global
    // de la ventana. Antes el bloqueo de F1 los tragaba aquí y el foco vive
    // casi siempre en este input, por eso morían todos los atajos.
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      const codigo = barcodeRef.current;

      if (!codigo) {
        justScannedRef.current = false;
        setBarcodeValue('');
        return;
      }

      // Rev 1 (23/09/2026): disparo inmediato sin esperar al backend; el
      // Frm resuelve por catálogo local/Redis y acumula en el buffer.
      if (onColectorScan && !disabled) {
        onColectorScan(codigo);
      }
      // Rev 5: NO se borra: se retiene seleccionado para repetir con otro
      // Enter o sobrescribir con la próxima lectura.
      justScannedRef.current = true;
      seleccionarLector();
      return;
    }

    if (event.key === 'Delete' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      justScannedRef.current = false;
      setBarcodeValue('');
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();
      if (!barcodeRef.current) return;
      // Rev 5: con el código retenido seleccionado, Backspace limpia todo.
      if (justScannedRef.current || tieneSeleccionTotal()) {
        justScannedRef.current = false;
        setBarcodeValue('');
        return;
      }
      const codex = barcodeRef.current.slice(0, -1);
      setBarcodeValue(codex);
      return;
    }

    const invalidKeys = [
      'ArrowUp','ArrowDown','ArrowRight','ArrowLeft','Insert','Home','End',
      'PageDown','PageUp','ContextMenu','AltGraph','Meta','Tab',
      'Alt','Control','Shift','CapsLock','NumLock','ScrollLock','Pause',
      'F1','F2','F3','F4','F5','F6','F7','F8','F9','F10','F11'
    ];

    if (invalidKeys.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if ((event.key || '').length !== 1) {
      return;
    }

    // La inserción la hace onChange (teclado físico, móvil, IME o pegado);
    // con el código retenido seleccionado el reemplazo es nativo.
    justScannedRef.current = false;
    return;
  };

  // Rev 2 (23/09/2026): mismo cascarón y estilo del colector anterior
  // (clases framedetallesventacodigobarrasentradassalidas + input con
  // maxWidth 200px) para conservar el tamaño en el pie fijo.
  return (
    <Box className="framedetallesventacodigobarrasentradassalidas"
      style={{
        width: '100%',
        height: '100%',
        margin: '0',
        padding: '0',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '4px',
      }}
    >
      <h2 className="framedetallesventalabelcodigobarras" style={{ margin: 0, padding: 0, fontSize: 'inherit' }}>
        Colector códigos
      </h2>
      <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%" }}>
        <input
          id="txtCodigoBarras"
          type="text"
          inputMode="text"
          enterKeyHint="enter"
          tabIndex={1}
          autoFocus
          autoComplete="off"
          name="lector-codigo-barras"
          aria-autocomplete="none"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore="true"
          className="txtCodigoBarras"
          ref={inputRef}
          value={barcode}
          onChange={handleChangeBarcode}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Escanear códigos aquí"}
          disabled={disabled}
          style={{ width: '100%', maxWidth: '200px' }}
        />
        <Tooltip title="Pegar">
          <span>
            <IconButton size="small" aria-label="Pegar" title="Pegar" onClick={handlePegarPortapapeles} disabled={disabled}>
              <ContentPaste fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </Box>
  );
});

export default SizeSoftXLectorCodigoBarrasEntradas;
